"use client";

import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { organizationsQueryKeys } from "@saas/organizations/lib/api";
import { AuthHeading } from "@saas/shared/components/AuthWrapper";
import { useRouter } from "@shared/hooks/router";
import { clearRouteContextCache } from "@shared/lib/route-context-cache";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { LinkButton } from "@ui/components/link-button";
import { Spinner } from "@ui/components/spinner";
import { toast } from "@ui/components/toast";
import { MailXIcon } from "lucide-react";
import { useState } from "react";
import { OrganizationLogo } from "./OrganizationLogo";

export const ROLE_COPY: Record<string, string> = {
	owner: "You'll join as an owner.",
	admin: "You'll join as an admin.",
	member: "You'll join as a member.",
};

type Props = { signedInAs: string } & (
	| {
			invitationId: string;
			organizationName: string;
			organizationSlug: string;
			logoUrl?: string;
			role?: string | null;
	  }
	| {
			/** A valid invitation sent to another email than the signed-in one. */
			invitationId: string;
			invitedEmail: string;
			organizationName: string;
			logoUrl?: string;
			/** Where the invited email signs in or creates its account, invitation kept. */
			continueTo: string;
	  }
	| { invitationId: null }
);

/**
 * Accept or decline an invitation, then open the retailer (or /app). Used by
 * the invitation page and the no-team screen.
 */
export function useAnswerInvitation() {
	const router = useRouter();
	const queryClient = useQueryClient();
	const [pending, setPending] = useState<{
		invitationId: string;
		accept: boolean;
	} | null>(null);

	const answer = async (
		invitationId: string,
		organizationSlug: string,
		accept: boolean,
	) => {
		setPending({ invitationId, accept });
		try {
			const { error } = accept
				? await authClient.organization.acceptInvitation({
						invitationId,
					})
				: await authClient.organization.rejectInvitation({
						invitationId,
					});
			if (error) {
				throw error;
			}
			await queryClient.invalidateQueries({
				queryKey: organizationsQueryKeys.list(),
			});
			// The layouts reuse their organization list while the app stays mounted;
			// without this the new member lands on "not on a team yet".
			clearRouteContextCache();
			if (accept) {
				router.replace(`/app/${organizationSlug}`);
				return;
			}
			// With no team left /app comes back to the no-team screen, reloaded.
			router.replace("/app");
			setPending(null);
		} catch (error) {
			toast.add({
				type: "error",
				title: accept
					? "Couldn't accept the invitation"
					: "Couldn't decline the invitation",
				description:
					error instanceof Error && error.message
						? error.message
						: "Try again in a moment.",
			});
			setPending(null);
		}
	};

	return { pending, answer };
}

/**
 * The page new retailer staff land on from their invitation email. Named
 * after the modal it used to be; it's a full page inside AuthWrapper now.
 */
export function OrganizationInvitationModal(props: Props) {
	const { pending: answering, answer } = useAnswerInvitation();
	const [confirmingDecline, setConfirmingDecline] = useState(false);

	if (props.invitationId === null) {
		return <InvalidInvitation signedInAs={props.signedInAs} />;
	}
	if ("invitedEmail" in props) {
		return <OtherEmailInvitation {...props} />;
	}

	const { invitationId, organizationName, organizationSlug, logoUrl, role } =
		props;
	const pending = answering
		? answering.accept
			? "accept"
			: "decline"
		: null;

	return (
		<div className="flex flex-col">
			<OrganizationLogo
				name={organizationName}
				logoUrl={logoUrl}
				className="mb-6 size-14 text-lg"
			/>
			<AuthHeading
				title={`Join ${organizationName} on ${config.appName}`}
				description={
					<>
						{ROLE_COPY[role ?? ""] ??
							"You'll help run their shopping assistant."}{" "}
						Signed in as{" "}
						<span className="font-medium text-foreground">
							{props.signedInAs}
						</span>
						.
					</>
				}
			/>

			{confirmingDecline ? (
				<div className="flex flex-col gap-4 rounded-3xl bg-card p-5 ring-1 ring-foreground/6 dark:ring-foreground/10">
					<div className="flex flex-col gap-1">
						<p className="font-medium text-sm">
							Decline this invitation?
						</p>
						<p className="text-muted-foreground text-sm">
							You'll need a new invitation to join later.
						</p>
					</div>
					<div className="flex flex-wrap justify-end gap-2">
						<Button
							variant="ghost"
							onClick={() => setConfirmingDecline(false)}
							disabled={pending !== null}
						>
							Keep it
						</Button>
						<Button
							variant="destructive"
							onClick={() =>
								answer(invitationId, organizationSlug, false)
							}
							disabled={pending !== null}
						>
							{pending === "decline" ? (
								<Spinner data-icon="inline-start" />
							) : null}
							Decline invitation
						</Button>
					</div>
				</div>
			) : (
				<div className="flex flex-col gap-2">
					<Button
						size="lg"
						onClick={() =>
							answer(invitationId, organizationSlug, true)
						}
						disabled={pending !== null}
					>
						{pending === "accept" ? (
							<Spinner data-icon="inline-start" />
						) : null}
						Accept and open {organizationName}
					</Button>
					<Button
						variant="ghost"
						size="lg"
						onClick={() => setConfirmingDecline(true)}
						disabled={pending !== null}
					>
						Decline
					</Button>
				</div>
			)}
		</div>
	);
}

function OtherEmailInvitation({
	invitedEmail,
	organizationName,
	logoUrl,
	continueTo,
	signedInAs,
}: {
	invitedEmail: string;
	organizationName: string;
	logoUrl?: string;
	continueTo: string;
	signedInAs: string;
}) {
	const [signingOut, setSigningOut] = useState(false);

	return (
		<div className="flex flex-col">
			<OrganizationLogo
				name={organizationName}
				logoUrl={logoUrl}
				className="mb-6 size-14 text-lg"
			/>
			<AuthHeading
				title={`Join ${organizationName} on ${config.appName}`}
				description={
					<>
						This invitation is for{" "}
						<span className="font-medium text-foreground">
							{invitedEmail}
						</span>
						. You're signed in as{" "}
						<span className="font-medium text-foreground">
							{signedInAs}
						</span>
						.
					</>
				}
			/>
			<div className="flex flex-col gap-2">
				<Button
					size="lg"
					disabled={signingOut}
					onClick={() => {
						setSigningOut(true);
						authClient.signOut({
							fetchOptions: {
								onSuccess: () => {
									// A full load so no layout keeps the old session.
									window.location.href = continueTo;
								},
								onError: () => setSigningOut(false),
							},
						});
					}}
				>
					{signingOut ? <Spinner data-icon="inline-start" /> : null}
					Sign out and continue
				</Button>
				<LinkButton to="/app" variant="ghost" size="lg">
					Stay signed in
				</LinkButton>
			</div>
		</div>
	);
}

function InvalidInvitation({ signedInAs }: { signedInAs: string }) {
	const [signingOut, setSigningOut] = useState(false);

	return (
		<div className="flex flex-col">
			<span className="mb-6 grid size-11 place-items-center rounded-2xl bg-muted text-muted-foreground [&_svg]:size-5">
				<MailXIcon />
			</span>
			<AuthHeading
				title="This invitation can't be used"
				description={
					<>
						It expired, was withdrawn, or was sent to an address
						other than{" "}
						<span className="font-medium text-foreground">
							{signedInAs}
						</span>
						.
					</>
				}
			/>
			<div className="flex flex-col gap-2">
				<LinkButton to="/app" size="lg">
					Open the admin
				</LinkButton>
				<Button
					variant="ghost"
					size="lg"
					disabled={signingOut}
					onClick={() => {
						setSigningOut(true);
						authClient.signOut({
							fetchOptions: {
								onSuccess: () => {
									window.location.href =
										config.auth.redirectAfterLogout;
								},
							},
						});
					}}
				>
					{signingOut ? <Spinner data-icon="inline-start" /> : null}
					Sign in with a different email
				</Button>
			</div>
		</div>
	);
}
